import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  IconButton,
  Typography,
  keyframes,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import CustomChips from "./components/customChips";
import DashboardTiles from "./components/dashboardTiles";
import Activities from "./components/activities";
import DynamicDataGrid from "../../components/dynamicDataGrid";
import { Column } from "@ant-design/plots";
import CandidateDashboard from "./candidateDashboard";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";

const shimmerAnimation = keyframes`
  0% { background-position: -800px 0; }
  100% { background-position: 800px 0; }
`;

const ShimmerBox = ({
  width = "100%",
  height = 40,
  borderRadius = 8,
  sx = {},
}) => (
  <Box
    sx={{
      width,
      height,
      borderRadius: `${borderRadius}px`,
      background:
        "linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 50%, #e8e8e8 75%)",
      backgroundSize: "800px 100%",
      animation: `${shimmerAnimation} 1.4s infinite linear`,
      flexShrink: 0,
      ...sx,
    }}
  />
);

// ── Jobs Tiles shimmer — matches actual compact tile dimensions ──────────────
const OpenJobsTileShimmer = () => (
  <Box
    sx={{
      backgroundColor: "#F0F7EE",
      p: "12px 14px",
      borderRadius: "10px",
      border: "1px solid #E2E8F0",
      width: "30%",
      boxSizing: "border-box",
    }}
  >
    {/* Title row */}
    <ShimmerBox width={80} height={12} borderRadius={3} sx={{ mb: 1 }} />
    <Box sx={{ display: "flex", gap: 1 }}>
      {/* Internal tile (highlighted green) */}
      <Box
        sx={{
          flex: 1,
          p: "10px 12px",
          border: "1px solid #C8E6C9",
          borderRadius: "8px",
          backgroundColor: "#64AE6820",
          display: "flex",
          flexDirection: "column",
          gap: "7px",
        }}
      >
        <ShimmerBox width="55%" height={22} borderRadius={3} />
        <ShimmerBox width="70%" height={11} borderRadius={3} />
      </Box>
      {/* Public tile (white) */}
      <Box
        sx={{
          flex: 1,
          p: "10px 12px",
          border: "1px solid #C8E6C9",
          borderRadius: "8px",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          gap: "7px",
        }}
      >
        <ShimmerBox width="55%" height={22} borderRadius={3} />
        <ShimmerBox width="70%" height={11} borderRadius={3} />
      </Box>
    </Box>
  </Box>
);

const SingleJobTileShimmer = () => (
  <Box
    sx={{
      backgroundColor: "#F0F7EE",
      p: "12px 14px",
      borderRadius: "10px",
      border: "1px solid #E2E8F0",
      width: "22%",
      boxSizing: "border-box",
    }}
  >
    {/* Title + filter row */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1,
      }}
    >
      <ShimmerBox width={80} height={12} borderRadius={3} />
      <ShimmerBox width={82} height={24} borderRadius={5} />
    </Box>
    {/* Single sub-tile */}
    <Box
      sx={{
        p: "10px 12px",
        border: "1px solid #C8E6C9",
        borderRadius: "8px",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: "7px",
      }}
    >
      <ShimmerBox width="38%" height={22} borderRadius={3} />
      <ShimmerBox width="62%" height={11} borderRadius={3} />
    </Box>
  </Box>
);

const JobTilesShimmer = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 2,
      width: "100%",
    }}
  >
    <OpenJobsTileShimmer />
    <SingleJobTileShimmer />
    <SingleJobTileShimmer />
    <SingleJobTileShimmer />
  </Box>
);

// ── Chart shimmer — matches actual blue bar chart layout ──────────────────
const ChartShimmer = () => {
  // Mimic the actual recruiter chart bar distribution
  const bars = [68, 14, 11, 8, 4, 3, 3, 2, 1, 1];
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        pt: 1,
      }}
    >
      {/* Chart area */}
      <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* Y-axis labels */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            pb: "28px",
            pr: 1,
            width: 32,
            flexShrink: 0,
          }}
        >
          {[0, 1, 2, 3, 4, 5].map((_, i) => (
            <ShimmerBox key={i} width={22} height={10} borderRadius={3} />
          ))}
        </Box>

        {/* Bars + x-axis */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {/* Y-axis title (rotated) */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "flex-end",
              gap: "10px",
              pb: "4px",
            }}
          >
            {bars.map((h, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  height: "100%",
                  display: "flex",
                  alignItems: "flex-end",
                }}
              >
                <ShimmerBox
                  width="100%"
                  height={`${Math.max(h, 2)}%`}
                  borderRadius="4px 4px 0 0"
                  sx={{
                    minHeight: 4,
                    background:
                      "linear-gradient(90deg, #c2d9ef 25%, #b0cce6 50%, #c2d9ef 75%)",
                    backgroundSize: "800px 100%",
                    animation: `${shimmerAnimation} 1.4s infinite linear`,
                  }}
                />
              </Box>
            ))}
          </Box>
          {/* X-axis line */}
          <Box
            sx={{
              height: "1px",
              background: "#d0d0d0",
              mb: "8px",
              flexShrink: 0,
            }}
          />
          {/* X-axis labels */}
          <Box sx={{ display: "flex", gap: "10px", flexShrink: 0 }}>
            {bars.map((_, i) => (
              <ShimmerBox
                key={i}
                width="100%"
                height={11}
                borderRadius={3}
                sx={{ flex: 1 }}
              />
            ))}
          </Box>
          {/* X-axis title */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "8px",
              flexShrink: 0,
            }}
          >
            <ShimmerBox width={72} height={11} borderRadius={3} />
          </Box>
          {/* Scrollbar */}
          <Box sx={{ mt: "10px", flexShrink: 0 }}>
            <ShimmerBox width="48%" height={5} borderRadius={3} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Activity shimmer — always shown while activities load ─────────────────
const ActivityShimmer = () => (
  <Box
    sx={{ display: "flex", flexDirection: "column", gap: 2.5, px: 2, pt: 1 }}
  >
    {Array(6)
      .fill(0)
      .map((_, i) => (
        <Box
          key={i}
          sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
        >
          {/* Icon box — matches the briefcase icon container in screenshot */}
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              flexShrink: 0,
              background:
                "linear-gradient(90deg, #e8e8e8 25%, #d8d8d8 50%, #e8e8e8 75%)",
              backgroundSize: "800px 100%",
              animation: `${shimmerAnimation} 1.4s infinite linear`,
            }}
          />
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              pt: "2px",
            }}
          >
            {/* Timestamp line */}
            <ShimmerBox width="52%" height={11} borderRadius={3} />
            {/* Activity text line */}
            <ShimmerBox width="85%" height={14} borderRadius={3} />
          </Box>
        </Box>
      ))}
  </Box>
);

// ──────────────────────────────────────────────────────────────────────────

const Customdashboard = () => {
  const dispatch = useDispatch();
  const nav = useNavigate();
  const isEmployerManage = useSelector((state) => state.user.isEmployer);
  const role = useSelector((state) => state.user.role);
  const [filterType, setFilterType] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const isEmployer = useSelector((state) => state.user.isEmployer);

  const [selectedChip, setSelectedChip] = useState(searchParams.get("data"));
  const [breadcrumb, setBreadcrumb] = useState([""]);
  const [chartData, setChartData] = useState([]);
  const [jobType, setJobType] = useState("Internal");
  const [jobname, setJobname] = useState();
  const [JobDuration, setJobDuration] = useState("");
  const [Jobid, setJobid] = useState();
  const [barchartcolor, setBarchartcolor] = useState();
  const [showGraphDatagrid, setShowGraphDatagrid] = useState(false);
  const [Payloadforchart2, setPayloadforchart2] = useState({});
  const chartNumber = useRef(1);
  const JobidNotUpdatedAfterClick = useRef(true);
  const [barchartlabel, setBarchartlabel] = useState([]);

  // ── CHANGED: separate first-load shimmer from subsequent CircularProgress ──
  const isFirstLoad = useRef(true);
  const [loading, setLoading] = useState(true); // first-load shimmer
  const [chartLoading, setChartLoading] = useState(false); // subsequent fetches → CircularProgress
  const [tableLoading, setTableLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(true); // activities always shimmer until loaded

  const [activityData, setActivityData] = useState([]);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const dataGridRecruiterId = useRef(null);
  const dataGridJobId = useRef(null);
  const drillLevel = useRef("recruiter");

  const [jobData, setJobData] = useState([]);
  const [disableNext, setDisableNext] = useState(false);
  const [disablePrev, setDisablePrev] = useState(true);
  const [datagridCount, setDatagridCount] = useState(2);
  const [updateTrigger, setUpdateTrigger] = useState(false);
  const [columns, setColumns] = useState([]);

  const [candidateCounts, setCandidateCounts] = useState({
    EmployerPortalAssociated: 0,
    EmployerPortalNotAssociated: 0,
    JobezePortalAssociated: 0,
  });
  const [highlightedTile, setHighlightedTile] = useState(
    "Candidates Associated with Job",
  );
  const [candidateTimeDuration, setCandidateTimeDuration] = useState({
    EmployerPortalNotAssociated: "Last Week",
    JobezePortalTotal: "Last Week",
  });
  const isCandidateFirstLoad = useRef(true);
  const [anchorElCandidate, setAnchorElCandidate] = useState({
    employer: null,
    jobeze: null,
  });

  // Helper: set loading correctly based on whether this is first load or not
  const setLoadingState = (val) => {
    if (isFirstLoad.current) {
      setLoading(val);
    } else {
      setChartLoading(val);
    }
  };

  const handleJobClick = (Job_type, jobname) => {
    fetchTotalCount();
    setJobType(Job_type);
    setJobname(jobname);
    setJobDuration("");
    setSelectedTimeDuration({
      1: "Last Week",
      2: "Last Week",
      3: "Last Week",
      4: "Last Week",
    });
  };

  useEffect(() => {
    if (
      jobType &&
      (jobType.includes("Internal") ||
        jobType.includes("Public") ||
        jobType.includes("Recently Closed") ||
        jobType.includes("Newly Created") ||
        jobType.includes("Recently Cancelled"))
    ) {
      setPayloadforchart2({});
      if (showGraphDatagrid) {
        setDatagridCount(2);
        dataGridRecruiterId.current = null;
        dataGridJobId.current = null;
        drillLevel.current = "recruiter";
        handlePageChange({ page: 1 });
      } else {
        if (JobDuration.length === 0 && !showGraphDatagrid) {
          handleChartData();
          fetchTotalCount();
        } else {
          return;
        }
      }
    }
  }, [jobType, jobname, showGraphDatagrid, JobDuration, selectedChip]);

  const chipItems = [];
  if (isEmployer) {
    chipItems.push(
      { name: "Jobs", icon: <WorkOutlineOutlinedIcon /> },
      { name: "Candidates", icon: <GroupOutlinedIcon /> },
    );
  }

  const handleChipClick = (chipName) => {
    if (chipName === "Candidates" || chipName === "Jobs")
      setSelectedChip(chipName);
    setSearchParams({ data: chipName });
  };

  const [anchorElDuration, setAnchorElDuration] = useState({});
  const [selectedTimeDuration, setSelectedTimeDuration] = useState({
    1: "Last Week",
    2: "Last Week",
    3: "Last Week",
    4: "Last Week",
  });

  const handleClickDuration = (event, jobType, value) => {
    setAnchorElDuration({
      ...anchorElDuration,
      [jobType]: event.currentTarget,
    });
  };

  const handleCloseDuration = (event, key, value) => {
    if (!event) {
      setAnchorElDuration((prev) => ({ ...prev, [key]: null }));
      return;
    }
    setSelectedTimeDuration((prev) => ({ ...prev, [key]: event }));
    setAnchorElDuration((prev) => ({ ...prev, [key]: null }));
    fetchTotalCount(event, value["jobType"]);
    setPayloadforchart2({});
    if (value["jobType"] === "Recently Closed") {
      setJobType("Recently Closed");
      setJobname("Closed Jobs");
      setJobDuration(event);
    } else if (value["jobType"] === "Recently Cancelled") {
      setJobType("Recently Cancelled");
      setJobname("Cancelled Jobs");
      setJobDuration(event);
    } else if (value["jobType"] === "Newly Created") {
      setJobType("Newly Created");
      setJobname("New Jobs");
      setJobDuration(event);
    }
    setUpdateTrigger(true);
  };

  useEffect(() => {
    if (updateTrigger && !showGraphDatagrid) {
      handleChartData();
      setUpdateTrigger(false);
    } else if (updateTrigger && showGraphDatagrid) {
      setDatagridCount(2);
      dataGridRecruiterId.current = null;
      dataGridJobId.current = null;
      drillLevel.current = "recruiter";
      handlePageChange({ page: 1 });
      setUpdateTrigger(false);
    }
  }, [updateTrigger]);

  const fetchCandidateCount = async () => {
    let portalname = "",
      duration = "";
    if (isCandidateFirstLoad.current) {
      isCandidateFirstLoad.current = false;
    } else {
      if (highlightedTile === "Total Candidates") {
        portalname = "Jobeze Portal";
        duration = candidateTimeDuration.JobezePortalTotal;
      } else {
        portalname = "Employer Portal";
        duration = candidateTimeDuration.EmployerPortalNotAssociated;
      }
    }
    try {
      const response = await axios.post(
        `dashboard/candidate-count`,
        { portal: portalname, time_span: duration },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      if (response.data.success) {
        const data = response.data.message;
        setCandidateCounts((prev) => {
          const updated = { ...prev };
          data.forEach((item) => {
            if (
              item.portal === "Employer Portal" &&
              item.Type === "Associated with Job"
            )
              updated.EmployerPortalAssociated = item.count;
            else if (
              item.portal === "Employer Portal" &&
              item.Type === "Not Associated with Job"
            )
              updated.EmployerPortalNotAssociated = item.count;
            else if (
              item.portal === "Jobeze Portal" &&
              item.Type === "Associated with Job"
            )
              updated.JobezePortalAssociated = item.count;
          });
          return updated;
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (selectedChip === "Candidates") fetchCandidateCount();
  }, [selectedChip, candidateTimeDuration, highlightedTile]);

  const fetchTotalCount = async (event, key) => {
    if (selectedChip === "Candidates") return;
    let payload = { job_type: key ? key : "", time_span: event ? event : "" };
    try {
      const response = await axios.post(`dashboard/job-count`, payload, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      const respData = response.data.message;
      if (respData?.length == 1) {
        let destructuredFiltrType = [...filterType];
        const filterIndex = destructuredFiltrType.findIndex(
          (item) => item.title === respData[0].title,
        );
        if (filterIndex !== -1)
          destructuredFiltrType[filterIndex] = respData[0];
        setFilterType(destructuredFiltrType);
      } else {
        setFilterType(respData);
      }
    } catch (error) {
      console.error("Error fetching job count:", error);
      setLoading(false);
    }
  };

  const fetchActivities = async (payload) => {
    if (selectedChip === "Candidates") return;
    if (!payload.job_type || !payload.time_span) return;
    setActivityLoading(true);
    try {
      const response = await axios.post(
        `dashboard/recent-activities`,
        payload,
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );

      setTimeout(() => {
        setActivityData(response.data.message);
        setActivityLoading(false);
      }, 300);
    } catch (error) {
      console.error(error);
      setActivityLoading(false);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleChartData = async () => {
    if (selectedChip === "Candidates") return;
    setLoadingState(true);
    const payload = {
      type: jobname ? jobname : "Open Jobs",
      section: jobType ? jobType : "Internal",
      duration: JobDuration ? JobDuration : "Last Week",
    };
    await axios
      .post("/job-recruiters", payload, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      })
      .then((response) => {
        fetchActivities({
          job_type: payload.section,
          time_span: payload.duration,
        });
        chartNumber.current = 2;
        setChartData(response.data.message);
        setBreadcrumb([
          payload.type || "Open Jobs",
          payload.section || "Internal",
          "Recruiters",
        ]);
        setBarchartcolor("#1976d2");
        JobidNotUpdatedAfterClick.current = true;
        setBarchartlabel(["Recruiters", "Jobs", "Job Count"]);
        // After first load, mark it done
        if (isFirstLoad.current) {
          isFirstLoad.current = false;
          setLoading(false);
        } else {
          setChartLoading(false);
        }
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
        setChartLoading(false);
      });
  };

  const handleAxisClick = async (data) => {
    if (selectedChip === "Candidates") return;
    setChartLoading(true);
    if (chartNumber.current == 1) {
      setChartLoading(false);
      return;
    }
    let CurrentJobids = null;
    if (JobidNotUpdatedAfterClick.current === false) {
      setJobid(data.axisValue);
      CurrentJobids = data.axisValue;
    }
    let currentAxisId = null,
      ChartPayload = {},
      ChartThreePayload = {};
    if (
      JobidNotUpdatedAfterClick.current === true ||
      chartNumber.current == 2
    ) {
      currentAxisId = data.axisValue;
      ChartPayload = {
        type: jobname || "Open Jobs",
        section: jobType || "Internal",
        duration: JobDuration || "Last Week",
        recruiter_id: currentAxisId,
      };
      setPayloadforchart2((prev) => ({ ...prev, ...ChartPayload }));
      if (currentAxisId) {
        if (chartNumber.current === 1 || chartNumber.current === 3) {
          setChartLoading(false);
          return;
        }
        await axios
          .post("/candidates-job", ChartPayload, {
            headers: {
              authorization:
                Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
            },
          })
          .then((response) => {
            fetchActivities({
              job_type: ChartPayload.section,
              time_span: ChartPayload.duration,
              recruiter_id: ChartPayload.recruiter_id,
            });
            setChartData(response.data.message);
            setBreadcrumb([
              ChartPayload.type || "Open Jobs",
              ChartPayload.section,
              "Recruiters",
              "Job Id",
            ]);
            setBarchartcolor("#3F95EA");
            JobidNotUpdatedAfterClick.current = false;
            chartNumber.current = 3;
            setBarchartlabel([
              "Job Name",
              "No. of Candidates",
              "Candidate Count",
            ]);
            setChartLoading(false);
          })
          .catch((error) => {
            console.error(error);
            setChartLoading(false);
          });
      } else {
        setChartLoading(false);
      }
    } else if (chartNumber.current === 3) {
      ChartThreePayload = {
        type: jobname || "Open Jobs",
        section: jobType || "Internal",
        duration: JobDuration || "Last Week",
        recruiter_id: Payloadforchart2.recruiter_id,
        job_id: CurrentJobids ? CurrentJobids : Jobid,
      };
      if (CurrentJobids) {
        if (chartNumber.current === 1 || chartNumber.current === 2) {
          setChartLoading(false);
          return;
        }
        await axios
          .post("/candidates-status-count", ChartThreePayload, {
            headers: {
              authorization:
                Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
            },
          })
          .then((response) => {
            fetchActivities({
              job_type: ChartThreePayload.section,
              time_span: ChartThreePayload.duration,
              recruiter_id: ChartThreePayload.recruiter_id,
              job_id: ChartThreePayload.job_id,
            });
            setChartData(response.data.message);
            setBreadcrumb([
              ChartThreePayload.type || "Open Jobs",
              ChartThreePayload.section,
              "Recruiters",
              "Job Id",
              "Candidate Status",
            ]);
            setBarchartcolor("#457EAB");
            CurrentJobids = null;
            JobidNotUpdatedAfterClick.current = true;
            chartNumber.current = 1;
            setBarchartlabel([
              "Candidate Status",
              "No. of Candidates",
              "Candidate Status",
            ]);
            setChartLoading(false);
          })
          .catch((error) => {
            console.error(error);
            setChartLoading(false);
          });
      } else {
        setChartLoading(false);
      }
    }
  };

  const fetchRecruiterTabularData = async (page) => {
    if (selectedChip === "Candidates") return;
    if (showGraphDatagrid) setTableLoading(true);
    else setLoadingState(true);
    const payload = {
      type: jobname || "Open Jobs",
      section: jobType || "Internal",
      duration: JobDuration || "Last Week",
    };
    try {
      const response = await axios.post(
        `/recruiter-tabular-data/${page}`,
        payload,
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      fetchActivities({
        job_type: payload.section,
        time_span: payload.duration,
      });
      const respData = response.data.message || [];
      setData(respData);
      setJobData([]);
      setColumns([
        { field: "id", headerName: "Id", width: 200 },
        { field: "name", headerName: "Name", width: 200 },
        { field: "job_count", headerName: "Job Count", width: 150 },
        { field: "candidate_count", headerName: "Candidate Count", width: 200 },
      ]);
      setDisableNext(respData?.length < pageSize);
      setDisablePrev(page === 1);
      setBreadcrumb([payload.type, payload.section, "Recruiters"]);
      setDatagridCount(2);
      dataGridRecruiterId.current = null;
      dataGridJobId.current = null;
      drillLevel.current = "recruiter";
    } catch (error) {
      console.error(error);
    } finally {
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
        setLoading(false);
      } else setChartLoading(false);
      setTableLoading(false);
    }
  };

  const fetchJobTabularData = async (recruiterId, page) => {
    if (selectedChip === "Candidates") return;
    setTableLoading(true);
    try {
      const payload = {
        type: jobname || "Open Jobs",
        section: jobType || "Internal",
        duration: JobDuration || "Last Week",
        recruiter_id: recruiterId,
      };
      const response = await axios.post(`/job-tabular-data/${page}`, payload, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      fetchActivities({
        job_type: payload.section,
        time_span: payload.duration,
        recruiter_id: payload.recruiter_id,
      });
      const respData = response.data.message || [];
      setJobData(respData);
      setColumns([
        { field: "id", headerName: "ID", width: 100 },
        { field: "name", headerName: "Name", width: 200 },
        {
          field: "Applied By Candidate",
          headerName: "Applied By Candidate",
          width: 200,
        },
        {
          field: "Applied By Employer",
          headerName: "Applied By Employer",
          width: 200,
        },
      ]);
      setDisableNext(respData?.length < pageSize);
      setDisablePrev(page === 1);
      setBreadcrumb([payload.type, payload.section, "Recruiters", "Job Id"]);
      setPayloadforchart2(payload);
      setDatagridCount(3);
      drillLevel.current = "job";
    } catch (error) {
      console.error(error);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchStatusTabularData = async (jobId, page) => {
    if (selectedChip === "Candidates") return;
    if (drillLevel.current !== "job") return;
    setTableLoading(true);
    try {
      const payload = {
        type: jobname || "Open Jobs",
        section: jobType || "Internal",
        duration: JobDuration || "Last Week",
        recruiter_id: dataGridRecruiterId.current,
        job_id: jobId,
      };
      const response = await axios.post(
        `/status-tabular-data/${page}`,
        payload,
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      fetchActivities({
        job_type: payload.section,
        time_span: payload.duration,
        recruiter_id: payload.recruiter_id,
        job_id: payload.job_id,
      });
      const respData = response.data.message || [];
      setJobData(respData);
      setColumns([
        { field: "id", headerName: "ID", width: 100 },
        { field: "status", headerName: "Status", width: 350 },
        { field: "count", headerName: "Count", width: 200 },
      ]);
      setDisableNext(respData?.length < pageSize);
      setDisablePrev(page === 1);
      setBreadcrumb([
        payload.type,
        payload.section,
        "Recruiters",
        "Job Id",
        "Candidate Status",
      ]);
      dataGridJobId.current = jobId;
      drillLevel.current = "status";
    } catch (error) {
      console.error(error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleRowClick = async (params) => {
    dataGridRecruiterId.current = params.row.id;
    dataGridJobId.current = null;
    setPage(1);
    fetchJobTabularData(params.row.id, 1);
  };

  const handleJobRowClick = async (params) => {
    if (drillLevel.current !== "job") return;
    setPage(1);
    await fetchStatusTabularData(params.row.id, 1);
  };

  const handlePageChange = (params) => {
    setPage(params.page);
    if (
      drillLevel.current === "status" &&
      dataGridRecruiterId.current &&
      dataGridJobId.current
    ) {
      fetchStatusTabularData(dataGridJobId.current, params.page);
    } else if (drillLevel.current === "job" && dataGridRecruiterId.current) {
      fetchJobTabularData(dataGridRecruiterId.current, params.page);
    } else {
      fetchRecruiterTabularData(params.page);
    }
  };

  const handleBreadcrumbClick = async (index) => {
    const slicedBreadcrumb = breadcrumb.slice(0, index + 1);
    setBreadcrumb(slicedBreadcrumb);
    if (slicedBreadcrumb?.length === 3) {
      if (showGraphDatagrid) {
        setDatagridCount(2);
        dataGridRecruiterId.current = null;
        dataGridJobId.current = null;
        drillLevel.current = "recruiter";
        setJobData([]);
        fetchRecruiterTabularData(1);
        setPage(1);
      } else {
        setBarchartcolor("#1976d2");
        handleChartData();
        setPayloadforchart2({});
      }
    } else if (slicedBreadcrumb?.length === 4) {
      if (showGraphDatagrid) {
        dataGridJobId.current = null;
        drillLevel.current = "job";
        setDatagridCount(3);
        setPage(1);
        fetchJobTabularData(dataGridRecruiterId.current, 1);
      } else {
        setChartLoading(false);
        setBarchartcolor("#3F95EA");
        chartNumber.current = 2;
        handleAxisClick({ axisValue: Payloadforchart2?.recruiter_id });
      }
    }
  };

  const handleListClick = () => {
    setShowGraphDatagrid(true);
    setTableLoading(true);
    dataGridRecruiterId.current = null;
    dataGridJobId.current = null;
    drillLevel.current = "recruiter";
    setDatagridCount(2);
    fetchRecruiterTabularData(1);
  };

  const maxYaxis = (data) => {
    const yaxisCount = { max: 5, tickcount: 5 };
    const maxCount = Math.max(...data.map((item) => item.count));
    if (maxCount + 1 <= 5) {
      yaxisCount.max = maxCount + 1;
      yaxisCount.tickcount = maxCount + 1;
    } else {
      yaxisCount.max = maxCount + Math.floor(maxCount / 5);
    }
    return yaxisCount;
  };

  const yaxisCount = maxYaxis(chartData);

  const Handleratio = () => {
    const ratio = 10 / chartData?.length;
    return ratio >= 1 ? null : ratio;
  };

  const updatedChartData = chartData.map((item) => ({
    ...item,
    nameWithId: `${item.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} (${item.id})`,
  }));

  const getChartHeight = () => {
    const h = window.innerHeight;
    return h >= 900
      ? Math.round(h * 0.52)
      : h >= 700
        ? Math.round(h * 0.46)
        : Math.round(h * 0.42);
  };

  const [dimensions, setDimensions] = useState({
    chartHeight: getChartHeight(),
    gap: 3,
  });

  const handleResize = () => {
    setDimensions({
      chartHeight: getChartHeight(),
      gap:
        window.innerWidth >= 1600
          ? 2
          : window.innerWidth >= 1200
            ? 2
            : window.innerWidth >= 800
              ? 1
              : 0.5,
    });
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const ChartDataDisplay = () => ({
    smooth: true,
    data: updatedChartData,
    xField: "nameWithId",
    yField: "count",
    height: dimensions.chartHeight,
    style: { fill: barchartcolor, radiusTopLeft: 10, radiusTopRight: 10 },
    scrollbar: { x: { ratio: Handleratio() } },
    scale: {
      y: { tickCount: yaxisCount.tickcount, domainMax: yaxisCount.max },
    },
    animation: false,
    axis: {
      x: {
        labelSpacing: 10,
        labelFontSize: 14,
        labelFontWeight: 700,
        labelAlign: "center",
        labelTransform: "Capitalize",
        labelFormatter: (val) => {
          const mapping = {
            "Offer Accepted (2000)": "Off. Acc.",
            "Offer Rejected (3000)": "Off. Rej.",
            "Offer Released (2500)": "Off. Rel.",
            "Tech Evaluation (2200)": "Tech. Eval.",
            "Manager Evaluation (2300)": "Man. Eval.",
          };
          if (mapping[val]) return mapping[val];
          const parts = val.split(" ")[0];
          return parts.length > 1 ? parts : val;
        },
        title: barchartlabel[0],
        line: { style: { stroke: "#000", lineWidth: 1 } },
        grid: null,
      },
      y: {
        title: barchartlabel[1],
        labelSpacing: 4,
        labelFontSize: 14,
        labelFontWeight: 700,
        line: { style: { stroke: "#000", lineWidth: 1 } },
        grid: null,
      },
    },
    onReady: ({ chart }) => {
      chart.on(`interval:click`, (ev) => {
        handleAxisClick({ axisValue: ev.data.data.id });
      });
    },
  });

  const buildJobsTiles = (key, value) => {
    const total = (value.Internal || 0) + (value.Public || 0);
    const tilesMap = {
      "Open Jobs": [
        {
          count: value.Internal,
          total,
          label: "Internal",
          isSelected: jobType === "Internal",
          onClick: () => handleJobClick("Internal", "Open Jobs"),
        },
        {
          count: value.Public,
          total,
          label: "Public",
          isSelected: jobType === "Public",
          onClick: () => handleJobClick("Public", "Open Jobs"),
        },
      ],
      "Closed Jobs": [
        {
          count: value.count,
          label: "Recently Closed",
          isSelected: jobType === "Recently Closed",
          onClick: () => handleJobClick("Recently Closed", "Closed Jobs"),
        },
      ],
      "New Jobs": [
        {
          count: value.count,
          label: "Newly Created",
          isSelected: jobType === "Newly Created",
          onClick: () => handleJobClick("Newly Created", "New Jobs"),
        },
      ],
      "Cancelled Jobs": [
        {
          count: value.count,
          label: "Recently Cancelled",
          isSelected: jobType === "Recently Cancelled",
          onClick: () => handleJobClick("Recently Cancelled", "Cancelled Jobs"),
        },
      ],
    };
    return tilesMap[value.title] || [];
  };

  const widthMap = {
    "Open Jobs": "30%",
    "Closed Jobs": "22%",
    "New Jobs": "22%",
    "Cancelled Jobs": "22%",
  };

  const candidateTotal =
    candidateCounts.EmployerPortalAssociated +
    candidateCounts.EmployerPortalNotAssociated;

  const handleEmployerTileTimeDuration = (data) => {
    if (!data) return;
    if (candidateTimeDuration.EmployerPortalNotAssociated === data) return;
    if (highlightedTile === "Total Candidates")
      setHighlightedTile("Candidates Associated with Job");
    setCandidateTimeDuration((prev) => ({
      ...prev,
      EmployerPortalNotAssociated: data,
    }));
  };

  const handleJobezeTileTimeDuration = (data) => {
    if (!data) return;
    if (candidateTimeDuration.JobezePortalTotal === data) return;
    setHighlightedTile("Total Candidates");
    setCandidateTimeDuration((prev) => ({ ...prev, JobezePortalTotal: data }));
  };

  const activeRows = drillLevel.current === "recruiter" ? data : jobData;
  const paginationModel = { page: page - 1, pageSize };
  const handlePaginationModelChange = (newModel) =>
    handlePageChange({ page: newModel.page + 1 });

  const handleDynamicRowClick = (row) => {
    if (drillLevel.current === "recruiter") handleRowClick({ row });
    else if (drillLevel.current === "job") handleJobRowClick({ row });
  };

  const totalRows = disableNext
    ? (page - 1) * pageSize + activeRows.length
    : page * pageSize + 1;

  // Whether to show shimmer (first load) or CircularProgress (subsequent)
  const showChartShimmer = loading && isFirstLoad.current;
  const showChartCircular = chartLoading && !isFirstLoad.current;

  const renderBreadcrumb = () =>
    breadcrumb.map((crumb, index) => {
      const isLast = index === breadcrumb.length - 1;
      const isContext = index === 0 || index === 1;
      if (isContext)
        return (
          <Typography
            key={index}
            sx={{
              fontSize: "1rem",
              fontWeight: 400,
              color: "#9AA2B1",
              lineHeight: 1.4,
            }}
          >
            {crumb}
          </Typography>
        );
      return (
        <Typography
          key={index}
          onClick={() => handleBreadcrumbClick(index)}
          sx={{
            fontSize: "1rem",
            fontWeight: 700,
            color: isLast ? "#3F95EA" : "#1A202C",
            cursor: "pointer",
            lineHeight: 1.4,
            transition: "color 0.15s",
            "&:hover": { color: "#3F95EA" },
          }}
        >
          {crumb}
        </Typography>
      );
    });

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#E3F2FD",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <CustomChips
            chipItems={chipItems}
            handleClick={handleChipClick}
            selectedChip={selectedChip}
          />

          {/* Jobs tiles */}
          {selectedChip === "Jobs" && (
            <Box
              gap={dimensions.gap}
              sx={{
                display: "flex",
                borderRadius: "10px",
                justifyContent: "space-between",
              }}
            >
              {filterType.length === 0 ? (
                <JobTilesShimmer />
              ) : (
                Object.entries(filterType).map(([key, value]) => (
                  <DashboardTiles
                    key={key}
                    title={value.title}
                    tiles={buildJobsTiles(key, value)}
                    showFilter={value.title !== "Open Jobs"}
                    filterProps={{
                      anchorEl: anchorElDuration[key],
                      open: Boolean(anchorElDuration[key]),
                      handleClose: (event) =>
                        handleCloseDuration(event, key, value),
                      selectedTime: selectedTimeDuration[key],
                      handleClick: (event) =>
                        handleClickDuration(event, key, value),
                      setAnchorElDuration,
                    }}
                    variant="jobs"
                    width={widthMap[value.title] || "22%"}
                  />
                ))
              )}
            </Box>
          )}

          {/* Candidate tiles */}
          {selectedChip === "Candidates" && (
            <Box
              sx={{
                display: "flex",
                borderRadius: "10px",
                justifyContent: "space-between",
                gap: dimensions.gap,
              }}
            >
              <Box sx={{ width: "70%" }}>
                <DashboardTiles
                  title="Employer Portal"
                  tiles={[
                    {
                      count: candidateCounts.EmployerPortalAssociated,
                      total: candidateTotal,
                      label: "Candidates Associated with Job",
                      isSelected:
                        highlightedTile === "Candidates Associated with Job",
                      onClick: () =>
                        setHighlightedTile("Candidates Associated with Job"),
                    },
                    {
                      count: candidateCounts.EmployerPortalNotAssociated,
                      total: candidateTotal,
                      label: "Candidates not Associated with Job",
                      isSelected:
                        highlightedTile ===
                        "Candidates not Associated with Job",
                      onClick: () =>
                        setHighlightedTile(
                          "Candidates not Associated with Job",
                        ),
                    },
                  ]}
                  showFilter={true}
                  filterProps={{
                    anchorEl: anchorElCandidate.employer,
                    open: Boolean(anchorElCandidate.employer),
                    handleClick: (event) =>
                      setAnchorElCandidate((prev) => ({
                        ...prev,
                        employer: event.currentTarget,
                      })),
                    handleClose: (data) => {
                      setAnchorElCandidate((prev) => ({
                        ...prev,
                        employer: null,
                      }));
                      handleEmployerTileTimeDuration(data);
                    },
                    selectedTime:
                      candidateTimeDuration.EmployerPortalNotAssociated,
                    setAnchorElDuration: setAnchorElCandidate,
                  }}
                  variant="candidates"
                  width="100%"
                />
              </Box>
              <Box sx={{ width: "30%" }}>
                <DashboardTiles
                  title="Jobeze Portal"
                  tiles={[
                    {
                      count: candidateCounts.JobezePortalAssociated,
                      label: "Total Candidates",
                      isSelected: highlightedTile === "Total Candidates",
                      onClick: () => setHighlightedTile("Total Candidates"),
                    },
                  ]}
                  showFilter={true}
                  filterProps={{
                    anchorEl: anchorElCandidate.jobeze,
                    open: Boolean(anchorElCandidate.jobeze),
                    handleClick: (event) =>
                      setAnchorElCandidate((prev) => ({
                        ...prev,
                        jobeze: event.currentTarget,
                      })),
                    handleClose: (data) => {
                      setAnchorElCandidate((prev) => ({
                        ...prev,
                        jobeze: null,
                      }));
                      handleJobezeTileTimeDuration(data);
                    },
                    selectedTime: candidateTimeDuration.JobezePortalTotal,
                    setAnchorElDuration: setAnchorElCandidate,
                  }}
                  variant="candidates"
                  width="100%"
                />
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ height: "100%", overflowY: "hidden" }}>
          {selectedChip === "Jobs" && (
            <Box sx={{ height: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  height: "100%",
                }}
              >
                {/* Chart / DataGrid panel */}
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: 2,
                    flexGrow: 1,
                    width: "70%",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "hidden",
                  }}
                >
                  {/* Header breadcrumb + toggle — hide during first-load shimmer */}
                  {(!loading || showGraphDatagrid) && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: "#F9F9F9",
                        padding: "8px 16px",
                        borderRadius: "10px 10px 0 0",
                        minHeight: "45px",
                        flexShrink: 0,
                        mb: 1.5,
                      }}
                    >
                      <Breadcrumbs
                        separator={
                          <Typography
                            sx={{
                              color: "#9AA2B1",
                              fontWeight: 400,
                              fontSize: "1rem",
                            }}
                          >
                            ›
                          </Typography>
                        }
                        sx={{
                          "& .MuiBreadcrumbs-ol": {
                            flexWrap: "nowrap",
                            alignItems: "center",
                          },
                        }}
                      >
                        {renderBreadcrumb()}
                      </Breadcrumbs>
                      {/* Toggle pill */}
                      <Box
                        sx={{
                          display: "flex",
                          border: "1px solid #D1D5DB",
                          borderRadius: "8px",
                          overflow: "hidden",
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          onClick={() => {
                            setShowGraphDatagrid(false);
                            setJobDuration("");
                            setSelectedTimeDuration({
                              1: "Last Week",
                              2: "Last Week",
                              3: "Last Week",
                              4: "Last Week",
                            });
                          }}
                          sx={{
                            width: 34,
                            height: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            backgroundColor: !showGraphDatagrid
                              ? "#3F95EA"
                              : "#fff",
                            borderRight: "1px solid #D1D5DB",
                            "&:hover": {
                              backgroundColor: !showGraphDatagrid
                                ? "#006CB7"
                                : "#f5f7fa",
                            },
                            transition: "background 0.15s",
                          }}
                        >
                          <BarChartOutlinedIcon
                            sx={{
                              fontSize: 18,
                              color: !showGraphDatagrid ? "#fff" : "#9AA2B1",
                            }}
                          />
                        </Box>
                        <Box
                          onClick={handleListClick}
                          sx={{
                            width: 34,
                            height: 30,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            backgroundColor: showGraphDatagrid
                              ? "#3F95EA"
                              : "#fff",
                            "&:hover": {
                              backgroundColor: showGraphDatagrid
                                ? "#006CB7"
                                : "#f5f7fa",
                            },
                            transition: "background 0.15s",
                          }}
                        >
                          <FormatListBulletedIcon
                            sx={{
                              fontSize: 18,
                              color: showGraphDatagrid ? "#fff" : "#9AA2B1",
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <Box
                    className="chart-wrapper"
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      px: 2,
                      pb: 1,
                    }}
                  >
                    {/* First load shimmer */}
                    {!showGraphDatagrid && loading && <ChartShimmer />}

                    {/* Subsequent loads → CircularProgress centered */}
                    {!showGraphDatagrid && !loading && chartLoading && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: dimensions.chartHeight,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    )}

                    {/* Chart */}
                    {!showGraphDatagrid && !loading && !chartLoading && (
                      <Column
                        className="dashboardCharts"
                        {...ChartDataDisplay()}
                      />
                    )}

                    {/* DataGrid */}
                    {showGraphDatagrid && (
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          height: "100%",
                          width: "100%",
                        }}
                      >
                        <DynamicDataGrid
                          rows={activeRows}
                          columns={columns}
                          loading={tableLoading}
                          totalRows={totalRows}
                          paginationModel={paginationModel}
                          onPaginationModelChange={handlePaginationModelChange}
                          onRowSelection={handleDynamicRowClick}
                          hideFooter={false}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Recent Activities panel */}
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: 2,
                    width: "30%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 400,
                      background: "#F9F9F9",
                      p: "12px",
                      borderRadius: "8px 8px 0 0",
                    }}
                    textAlign="start"
                  >
                    Recent Activities
                  </Typography>
                  <Box
                    sx={{
                      height: "100%",
                      position: "relative",
                      overflowY: "auto",
                      px: 2,
                    }}
                  >
                    {/* Activity shimmer always shows until first load complete */}
                    {activityLoading ? (
                      <ActivityShimmer />
                    ) : (
                      <Activities
                        activityData={activityData}
                        loading={false}
                        onItemClick={(item) => {
                          if (item?.jobId) nav(`/jobs/details/${item.jobId}`);
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {selectedChip === "Candidates" && (
            <CandidateDashboard
              counts={candidateCounts}
              highlightedTile={highlightedTile}
              onTileClick={setHighlightedTile}
              timeDuration={candidateTimeDuration}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default Customdashboard;
